import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import type { BoothTrack } from '../panels';

export type BasketAddStatus = 'idle' | 'pending' | 'added' | 'error';

/** One flavour as the VTS basket endpoint takes it. */
export interface BasketPositionInput {
  /** Shop id of the product attribute (flavour). */
  product_attribute_id: number;
  quantity: number;
  /** VTS price per unit. */
  price: number;
}

export interface BasketAddMeta {
  /** ERP id of the item (product size) the flavours belong to. */
  productSizeId: number;
}

export interface BoothBasket {
  /** Adds the flavours of one item; resolves true on success. */
  addPositions: (
    positions: BasketPositionInput[],
    meta: BasketAddMeta
  ) => Promise<boolean>;
  /** Status of the last add per item (ERP id of the product size). */
  statusByProductSizeId: Record<number, BasketAddStatus>;
}

/** How long "Added" stays on the button. */
const ADDED_RESET_MS = 2500;

/** Posts flavours to the VTS basket; also used by the new products of the Highlights tab. */
export function postPositions(
  positions: BasketPositionInput[]
): Promise<unknown> {
  const formData = new FormData();
  formData.append('positionsJson', JSON.stringify(positions));

  return axios.post('/api/onlineshop/vts-basket-add-position', formData, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
}

/**
 * Adds flavours to the VTS basket, one item at a time, and keeps a status per item. Quantities
 * are unrestricted: the endpoint has no stock gate and adds to what the basket already holds.
 */
export default function useBoothBasket(
  track: BoothTrack,
  refetchBasket: () => Promise<void>
): BoothBasket {
  const [statusByProductSizeId, setStatusByProductSizeId] = useState<
    Record<number, BasketAddStatus>
  >({});
  const resetTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const { mutateAsync } = useMutation({ mutationFn: postPositions });

  useEffect(() => {
    const timers = resetTimers.current;

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const setStatus = useCallback(
    (productSizeId: number, status: BasketAddStatus) => {
      const timer = resetTimers.current.get(productSizeId);
      if (timer !== undefined) {
        clearTimeout(timer);
        resetTimers.current.delete(productSizeId);
      }
      setStatusByProductSizeId((prev) =>
        prev[productSizeId] === status
          ? prev
          : { ...prev, [productSizeId]: status }
      );
    },
    []
  );

  const addPositions = useCallback(
    async (
      positions: BasketPositionInput[],
      { productSizeId }: BasketAddMeta
    ): Promise<boolean> => {
      const units = positions.reduce(
        (sum, position) => sum + position.quantity,
        0
      );
      if (units <= 0) {
        return false;
      }

      setStatus(productSizeId, 'pending');
      try {
        await mutateAsync(positions);
      } catch (error) {
        setStatus(productSizeId, 'error');
        return false;
      }

      track('basket_added', { product_size_id: productSizeId, units });
      setStatus(productSizeId, 'added');
      resetTimers.current.set(
        productSizeId,
        setTimeout(() => {
          resetTimers.current.delete(productSizeId);
          setStatusByProductSizeId((prev) =>
            prev[productSizeId] === 'added'
              ? { ...prev, [productSizeId]: 'idle' }
              : prev
          );
        }, ADDED_RESET_MS)
      );
      // The add went through; a failed refresh only delays "In basket".
      refetchBasket().catch(() => undefined);

      return true;
    },
    [mutateAsync, refetchBasket, setStatus, track]
  );

  return { addPositions, statusByProductSizeId };
}
